import apiClient from './client'
import type { PaginatedResponse } from '@/types'

// Types
export interface Event {
  id: number
  title: string
  slug: string
  description?: string
  short_description: string
  image: string | null
  thumbnail?: string | null
  type: string  // webinar, workshop, conference, community_day, training, meeting, other
  mode: 'online' | 'in_presenza' | 'ibrido'  // Event mode
  start_datetime: string
  end_datetime: string
  // Compatibility aliases
  start_date?: string
  end_date?: string
  location: string | null
  is_online: boolean
  is_public?: boolean
  is_free?: boolean
  jitsi_room_name?: string | null
  jitsi_url?: string | null  // Backend field name
  max_participants: number | null
  registrations_count: number
  registered_count?: number  // Alias
  status: 'draft' | 'published' | 'cancelled' | 'completed'
  category?: number
  category_name?: string
  organizer: number | {
    id: number
    full_name: string
    avatar: string | null
  }
  organizer_name?: string
  organizer_details?: {
    id: number
    full_name: string
    avatar: string | null
  }
  is_registered?: boolean
  is_past?: boolean
  is_ongoing?: boolean
  is_upcoming?: boolean
  created_at?: string
}

export interface EventRegistration {
  id: number
  event: Event
  user_id: number
  status: 'pending' | 'confirmed' | 'cancelled'
  registered_at: string
  attended: boolean
}

export interface EventAgenda {
  id: number
  event_id: number
  time: string
  title: string
  description: string
  speaker: string | null
}

export interface EventMaterial {
  id: number
  event_id: number
  title: string
  file_url: string
  file_type: string
  uploaded_at: string
}

export interface CommunityDayProposal {
  id: number
  title: string
  description: string
  topic: string
  proposed_by: {
    id: number
    full_name: string
  }
  status: 'pending' | 'approved' | 'rejected'
  votes_count: number
  has_voted?: boolean
  created_at: string
}

export interface EventCategory {
  id: number
  name: string
  slug: string
  description: string
  color: string
  icon: string
  events_count: number
}

// Calendar Event (simplified for calendar view)
export interface CalendarEvent {
  id: number
  title: string
  slug: string
  type: string
  start_datetime: string
  end_datetime: string
  is_online: boolean
  status: string
  is_registered?: boolean
  category_color?: string
}

export const eventsApi = {
  // Categories
  getCategories: async (): Promise<EventCategory[]> => {
    const response = await apiClient.get('/events/categories/')
    if (Array.isArray(response.data)) {
      return response.data
    }
    return response.data.results || []
  },

  // Events
  getEvents: async (params?: {
    type?: string
    category?: string
    mode?: string
    status?: string
    search?: string
    from_date?: string
    to_date?: string
    upcoming?: boolean  // Show only future events
    past?: boolean      // Show only past events
    page?: number
  }): Promise<PaginatedResponse<Event>> => {
    const response = await apiClient.get('/events/events/', { params })
    return response.data
  },

  // Calendar view
  getCalendarEvents: async (start: string, end: string): Promise<CalendarEvent[]> => {
    const response = await apiClient.get('/events/events/calendar/', {
      params: { start, end }
    })
    return response.data
  },

  getEvent: async (slug: string): Promise<Event & {
    agenda_items: EventAgenda[]
    materials: EventMaterial[]
  }> => {
    const response = await apiClient.get(`/events/events/${slug}/`)
    return response.data
  },

  getUpcomingEvents: async (limit?: number): Promise<Event[]> => {
    // Use main events endpoint with ordering and limit
    const response = await apiClient.get('/events/events/', {
      params: {
        from_date: new Date().toISOString().split('T')[0],
        ordering: 'start_date',
        page_size: limit || 5
      }
    })
    return response.data.results || []
  },

  // Registration - use slug instead of id
  registerForEvent: async (slug: string): Promise<EventRegistration> => {
    const response = await apiClient.post(`/events/events/${slug}/register/`)
    return response.data
  },

  cancelRegistration: async (slug: string): Promise<void> => {
    await apiClient.post(`/events/events/${slug}/unregister/`)
  },

  getMyRegistrations: async (): Promise<EventRegistration[]> => {
    const response = await apiClient.get('/events/events/my-registrations/')
    return response.data
  },

  // Get user's upcoming registered events (for agenda widget)
  getMyUpcomingEvents: async (limit?: number): Promise<Event[]> => {
    const response = await apiClient.get('/events/events/', {
      params: {
        my_events: 'true',
        upcoming: 'true',
        ordering: 'start_datetime',
        page_size: limit || 10
      }
    })
    return response.data.results || []
  },

  // Jitsi - use slug
  getJitsiToken: async (slug: string): Promise<{
    room_name: string
    room_url: string
    event_title: string
    user_role: string
    is_moderator: boolean
    jwt?: string
    room_url_with_jwt?: string
    room_url_with_params?: string
  }> => {
    const response = await apiClient.get(`/events/events/${slug}/jitsi_token/`)
    return response.data
  },

  // Feedback - use slug
  submitFeedback: async (slug: string, data: {
    overall_rating: number
    content_rating?: number
    organization_rating?: number
    would_recommend?: boolean
    comments?: string
  }): Promise<void> => {
    await apiClient.post(`/events/events/${slug}/feedback/`, data)
  },

  // Community Day Proposals
  getProposals: async (params?: {
    status?: string
    page?: number
  }): Promise<PaginatedResponse<CommunityDayProposal>> => {
    const response = await apiClient.get('/events/proposals/', { params })
    return response.data
  },

  createProposal: async (data: {
    title: string
    description: string
    topic: string
  }): Promise<CommunityDayProposal> => {
    const response = await apiClient.post('/events/proposals/', data)
    return response.data
  },

  voteProposal: async (proposalId: number): Promise<void> => {
    await apiClient.post(`/events/proposals/${proposalId}/vote/`)
  },

  unvoteProposal: async (proposalId: number): Promise<void> => {
    await apiClient.delete(`/events/proposals/${proposalId}/vote/`)
  },
}
